// src/components/maestros/ModalTransportadoras.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { Col, Row } from 'reactstrap';
import Swal from 'sweetalert2';
import axios from '../../../utils/axiosConfig';
import ModalBaseSm from '../../modals/modal-base-sm';
import FormModalCreateTransportadora from '../forms/formModalCreateTransportadora';

const ModalTransportadoras = ({ bitInserts, bitEdits, bitDeletes }) => {
    // --- estado de datos ---
    const [transportadoras, setTransportadoras] = useState([]);

    // --- buscador y debounce ---
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);

    useEffect(() => {
        const handler = setTimeout(
            () => setDebouncedTerm(searchTerm.trim().toLowerCase()),
            300
        );
        return () => clearTimeout(handler);
    }, [searchTerm]);

    // --- lista filtrada ---
    const filtered = useMemo(() => {
        if (!debouncedTerm) return transportadoras;
        return transportadoras.filter(item =>
            Object.values(item).some(val =>
                String(val || '')
                    .toLowerCase()
                    .includes(debouncedTerm)
            )
        );
    }, [transportadoras, debouncedTerm]);

    // --- modal y formulario ---
    const [dialogOpen, setDialogOpen] = useState(false);
    const [nombre, setNombre] = useState('');
    const [alias, setAlias] = useState('');
    const [proyectoId, setProyectoId] = useState(null);
    const [idEdit, setIdEdit] = useState(-1);

    const [bitEdit, setBitEdit] = useState(false);
    const [bitDelete, setBitDelete] = useState(false);
    const [bitInsert, setBitInsert] = useState(false);

    const toggleDialog = () => {
        setDialogOpen(o => !o);
        if (dialogOpen) {
            setNombre('');
            setAlias('');
            setIdEdit(-1);
            setProyectoId(null);
        }
    };

    const listarTransportadoras = () => {
        axios
            .post(
                `${process.env.REACT_APP_SERVER}/maestros/gettransportadoras`,
                {},
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${localStorage.getItem('tkn')}`,
                    },
                    withCredentials: true,
                }
            )
            .then(res => {
                if (res.data.success) setTransportadoras(res.data.data);
                else Swal.fire('Error', 'No se pudo cargar transportadoras', 'error');
            })
            .catch(() => {
                Swal.fire('Error', 'Falla de red al cargar lista', 'error');
                window.location.reload();
            });
    };

    // --- carga inicial ---
    useEffect(() => {
        listarTransportadoras();
    }, []);

    useEffect(() => {
        const HandlePageByRole = async role => {
            const data = { pagina: role };

            try {
                const res = await axios.post(
                    process.env.REACT_APP_SERVER + '/usuarios/getconfiguracionpagina',
                    JSON.stringify(data),
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${localStorage.getItem('tkn')}`,
                        },
                        withCredentials: true,
                    }
                );

                if (res.data.success === false) {
                    window.location.href = '/';
                } else {
                    if (res.data.data.length > 0) {
                        // setBitOpen(res.data.data[0].bitOpen); // si lo usas, declara el estado
                        setBitInsert(res.data.data[0].bitInsert);
                        setBitEdit(res.data.data[0].bitEdit);
                        setBitDelete(res.data.data[0].bitDelete);

                        if (!res.data.data[0].bitOpen) {
                            Swal.fire({
                                title: 'Información',
                                text: 'No tiene los permisos necesarios para acceder a esta página',
                                icon: 'info',
                                showConfirmButton: false,
                                timer: 3000,
                                allowOutsideClick: false,
                                didOpen: () => {
                                    Swal.showLoading();
                                },
                            }).then(() => {
                                console.log('Error en permisos no mostrando');
                            });
                        }
                    }
                }
            } catch (error) {
                console.error('Error al listar bit permisos:', error);
            }
        };

        HandlePageByRole('30');
    }, []);

    // --- editar / borrar ---
    const editar = item => {
        console.log('Editando transportadora:', item);
        setNombre(item.NombreTranspo || '');
        setAlias(item.AliasTranspo || '');
        setIdEdit(item.IdTransportadora);
        setProyectoId(item.proyectoTransportadora || null);
        setDialogOpen(true);
    };

    const borrar = t => {
        Swal.fire({
            title: '¿Está seguro?',
            text: `Está a punto de eliminar la transportadora "${t.AliasTranspo}". Esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#006699',
        }).then(r => {
            if (r.isConfirmed) {
                axios
                    .post(
                        `${process.env.REACT_APP_SERVER}/operaciones2/deletetransportadora`,
                        JSON.stringify({ IdTransportadora: t.IdTransportadora }),
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('tkn')}`,
                            },
                            withCredentials: true,
                        }
                    )
                    .then(() => {
                        Swal.fire(
                            'Eliminado',
                            'La transportadora ha sido eliminada.',
                            'success'
                        );
                        setTransportadoras(ts =>
                            ts.filter(
                                item => item.IdTransportadora !== t.IdTransportadora
                            )
                        );
                    });
            }
        });
    };

    // --- guardar (create/update) ---
    const guardar = ({ nombre: n, alias: a, proyectoId: pId }) => {
        console.log('Guardando transportadora:', { n, a, pId });

        if (!n.trim() || !a.trim()) {
            Swal.fire(
                'Error',
                'Debe completar al menos Nombre y Alias de la transportadora',
                'error'
            );
            return;
        }

        if (idEdit === -1) {
            const payload = {
                NombreTranspo: n,
                AliasTranspo: a,
                ID: -1,
                proyectoTransportadora: pId,
            };
            console.log('Payload crear nueva transportadora:', payload);

            axios
                .post(
                    `${process.env.REACT_APP_SERVER}/operaciones2/settransportadora`,
                    JSON.stringify(payload),
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${localStorage.getItem('tkn')}`,
                        },
                        withCredentials: true,
                    }
                )
                .then(res => {
                    if (!res.data.success) {
                        Swal.fire(
                            'Error',
                            'No se pudo crear transportadora',
                            'error'
                        );
                        return;
                    }
                    Swal.fire('Éxito', 'Transportadora creada', 'success');
                    listarTransportadoras();
                    toggleDialog();
                })
                .catch(() =>
                    Swal.fire('Error', 'Error en servicio', 'error')
                );
        } else {
            console.log('Actualizando transportadora:', {
                n,
                a,
                idEdit,
                pId,
            });

            const payloadUpdate = {
                NombreTranspo: n,
                AliasTranspo: a,
                ID: idEdit,
                proyectoTransportadora: pId,
            };

            axios
                .post(
                    `${process.env.REACT_APP_SERVER}/operaciones2/settransportadora`,
                    JSON.stringify(payloadUpdate),
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${localStorage.getItem('tkn')}`,
                        },
                        withCredentials: true,
                    }
                )
                .then(res => {
                    if (!res.data.success) {
                        Swal.fire('Error', 'No se pudo actualizar', 'error');
                        return;
                    }
                    Swal.fire(
                        'Éxito',
                        'Transportadora actualizada',
                        'success'
                    );
                    toggleDialog();
                    listarTransportadoras();
                })
                .catch(() =>
                    Swal.fire('Error', 'Error en servicio', 'error')
                );
        }
    };

    return (
        <>
            {/* Estilos responsivos solo para este componente */}
            <style>
                {`
                    @media (max-width: 575.98px) {
                        .btn-nueva-transportadora {
                            width: 100%;
                        }
                    }
                `}
            </style>

            {/* BUSCADOR */}
            <Row className="mb-4 align-items-center">
                <Col sm="6">
                    <input
                        type="text"
                        className="form-input w-100"
                        placeholder="Buscar transportadora..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        style={{ height: '38px', boxSizing: 'border-box' }}
                    />
                </Col>
                <Col
                    sm="6"
                    className="d-flex justify-content-center justify-content-sm-end mt-2 mt-sm-0"
                >
                    <button
                        className="Button blue btn-nueva-transportadora"
                        onClick={() => {
                            if (bitInserts ?? bitInsert) toggleDialog();
                        }}
                        style={{ height: '38px', minWidth: '150px' }}
                    >
                        Nueva Transportadora
                    </button>
                </Col>
            </Row>

            {/* TABLA */}
            <div className="card ContainerTableMaestros">
                <div
                    className="table-responsive text-nowrap"
                    style={{ height: '300px', overflowY: 'auto' }}
                >
                    <table
                        className="table table-hover"
                        style={{ width: '100%' }}
                    >
                        <thead className="FondoHeaderTabla2">
                            <tr>
                                <th style={{ color: '#fff', width: '50%' }}>
                                    Nombre
                                </th>
                                <th style={{ color: '#fff', width: '30%' }}>
                                    Alias
                                </th>
                                <th
                                    style={{
                                        color: '#fff',
                                        width: '20%',
                                        textAlign: 'center',
                                    }}
                                >
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((t, i) => (
                                <tr key={i}>
                                    <td style={{ color: '#000' }}>
                                        {t.NombreTranspo || '-'}
                                    </td>
                                    <td style={{ color: '#000' }}>
                                        {t.AliasTranspo || '-'}
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <a
                                            href="#"
                                            onClick={e => {
                                                e.preventDefault();
                                                if (bitEdits ?? bitEdit)
                                                    editar(t);
                                            }}
                                        >
                                            <i
                                                className="bx bxs-pencil bx-tada"
                                                style={{ color: '#006699' }}
                                            />
                                        </a>{' '}
                                        <a
                                            href="#"
                                            onClick={e => {
                                                e.preventDefault();
                                                if (bitDeletes ?? bitDelete)
                                                    borrar(t);
                                            }}
                                        >
                                            <i
                                                className="bx bx-trash"
                                                style={{ color: '#006699' }}
                                            />
                                        </a>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MODAL */}
            <ModalBaseSm
                isOpen={dialogOpen}
                title={
                    idEdit === -1
                        ? 'Crear transportadora'
                        : 'Editar transportadora'
                }
                onClose={toggleDialog}
                childComponent={
                    <FormModalCreateTransportadora
                        onSave={guardar}
                        initialData={{ nombre, alias, proyectoId }}
                    />
                }
            />
        </>
    );
};

export default ModalTransportadoras;
